import { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { cn } from "@/lib/cn";

// Password field with a show/hide toggle and optional strength meter.
export default function PasswordField({
  label = "Password",
  id = "password",
  value,
  onChange,
  error,
  showStrength = false,
  ...props
}) {
  const [visible, setVisible] = useState(false);

  const strength = getStrength(value || "");

  return (
    <label className="block" htmlFor={id}>
      <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </span>
      <span className="relative block">
        <Lock
          size={18}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          className={cn(
            "h-11 w-full rounded-xl border border-slate-300 bg-white pl-11 pr-11 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500",
            error &&
              "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15"
          )}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        >
          {/* The icon reports the field's STATE, not the click's action: an open
              eye means the password is readable right now. The aria-label stays
              action-worded, which is what a screen reader needs from a button. */}
          {visible ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>
      </span>

      {showStrength && value && (
        <span className="mt-2 flex items-center gap-2">
          <span className="flex h-1.5 flex-1 gap-1">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={cn(
                  "h-full flex-1 rounded-full transition-colors",
                  i < strength.score ? strength.color : "bg-slate-200 dark:bg-white/10"
                )}
              />
            ))}
          </span>
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {strength.label}
          </span>
        </span>
      )}

      {error && (
        <span className="mt-1.5 block text-xs font-medium text-rose-500">
          {error}
        </span>
      )}
    </label>
  );
}

function getStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ["Weak", "Weak", "Fair", "Good", "Strong"];
  const colors = [
    "bg-rose-400",
    "bg-rose-400",
    "bg-amber-400",
    "bg-lime-400",
    "bg-emerald-500",
  ];
  return { score, label: labels[score], color: colors[score] };
}
